import { useMemo, useState } from "react";
import { toast } from "@/components/ui/notify";
import { Button } from "@/components/ui/button";
import type { MachineDetail } from "@/features/lines/hooks";
import { LinkSensorDialog, UnlinkSensorDialog } from "./LinkSensorDialog";
import { getParameterSourceFriendlyLabel, getSensorStatusLabel } from "@/lib/display-labels";
import { isDraftLineId } from "@/features/lines/canvas/draft-canvas-adapter";
import { useDraftStore } from "@/features/lines/draft/useLineDraftState";
import { selectDraftLinkedSensorIds } from "./hooks/bridge-selectors";
import { useMachineSidebarBridge } from "./hooks/useMachineSidebarBridge";
import type { SidebarMode } from "./MachineSidebar";

interface Props {
  machine: MachineDetail;
  lineId: string;
  mode: SidebarMode;
}

export function SensorsTab({ machine, lineId, mode }: Props) {
  const isView = mode === "view";
  const isDraft = isDraftLineId(lineId);
  const bridge = useMachineSidebarBridge(machine.id, lineId);

  // Item 4: em rascunho, sensores já vinculados a qualquer máquina do rascunho
  // não devem ser reoferecidos no modal de vínculo (derivação em bridge-selectors).
  const draftMachines = useDraftStore((s) => s.machines);
  const draftLinkedSensorIds = useMemo(
    () => selectDraftLinkedSensorIds(isDraft, draftMachines),
    [isDraft, draftMachines]
  );

  const [linkParamId, setLinkParamId] = useState<string | null>(null);
  const [unlinkTarget, setUnlinkTarget] = useState<{
    linkId: string;
    sensorId: string;
    label: string;
  } | null>(null);

  const allParameters = isDraft
    ? bridge.parameters.map((p) => ({
        id: p.id,
        name: p.name,
        source: p.source,
        unit: p.unit,
        influencesStatus: p.influencesStatus,
        currentValue: null as string | null,
        sensorLinks: bridge.sensorLinks
          .filter((sl) => sl.parameterId === p.id)
          .map((sl) => ({ id: sl.id, sensor: null }))
          .slice(0, 1),
      }))
    : (machine.parameters ?? []);

  const parameters = allParameters.filter((p) => p.source !== "MANUAL");

  if (parameters.length === 0) {
    const hasOnlyManual = allParameters.length > 0;
    return (
      <p className="text-sm text-muted-foreground">
        {hasOnlyManual
          ? 'Esta máquina só tem parâmetros do tipo "Manual" — nenhum aceita sensor. Cadastre um parâmetro sensorial em "Parâmetros".'
          : 'Esta máquina não possui parâmetros. Cadastre parâmetros em "Parâmetros" antes de vincular sensores.'}
      </p>
    );
  }

  return (
    <div className="space-y-2">
      <ul className="space-y-1.5">
        {parameters.map((p) => {
          const link = p.sensorLinks?.[0];
          const sensor = link?.sensor;
          const inconsistent = sensor?.status === "INCONSISTENT";
          return (
            <li key={p.id} className="space-y-1 rounded border px-2 py-1.5 text-sm">
              <div className="flex items-center justify-between gap-2">
                <div className="min-w-0 flex-1">
                  <p className="truncate font-medium">{p.name}</p>
                  <p className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground">
                    {getParameterSourceFriendlyLabel(
                      p.source as "SENSOR" | "MANUAL" | "IMPORT" | "CALCULATED" | "INTEGRATION"
                    )}
                    {p.influencesStatus ? " · gov. status" : ""}
                  </p>
                </div>
                <span className="font-mono text-sm">
                  {p.currentValue ?? "—"}
                  {p.unit ? ` ${p.unit}` : ""}
                </span>
              </div>
              {sensor ? (
                <div
                  className={`flex items-center justify-between rounded border px-2 py-1 text-xs ${
                    inconsistent
                      ? "border-rd-warning-500/30 bg-rd-warning-50"
                      : "border-muted bg-muted/10"
                  }`}
                >
                  <div className="min-w-0 flex-1">
                    <p className="font-mono">
                      {sensor.code} · {sensor.name}
                    </p>
                    {inconsistent ? (
                      <p className="text-warning">
                        Leitura inconsistente — conferir mapeamento de status no parâmetro.
                      </p>
                    ) : (
                      <p className="text-muted-foreground">
                        Status: {getSensorStatusLabel(sensor.status)}
                      </p>
                    )}
                  </div>
                  {!isView ? (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() =>
                        setUnlinkTarget({
                          linkId: link.id,
                          sensorId: sensor.id,
                          label: `${sensor.code} · ${sensor.name}`,
                        })
                      }
                    >
                      Desvincular
                    </Button>
                  ) : null}
                </div>
              ) : !isView ? (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setLinkParamId(p.id)}
                  className="w-full"
                >
                  + Vincular sensor a este parâmetro
                </Button>
              ) : (
                <p className="text-xs italic text-muted-foreground">Sem sensor vinculado.</p>
              )}
            </li>
          );
        })}
      </ul>

      <LinkSensorDialog
        parameterId={linkParamId}
        excludeSensorIds={draftLinkedSensorIds}
        open={!!linkParamId}
        onClose={() => setLinkParamId(null)}
        onLinkOverride={
          isDraft
            ? async (sensorId) => {
                if (!linkParamId) return;
                await bridge.linkSensor(linkParamId, sensorId);
                toast.success("Sensor vinculado ao rascunho.");
              }
            : undefined
        }
      />
      <UnlinkSensorDialog
        linkId={unlinkTarget?.linkId ?? null}
        sensorId={unlinkTarget?.sensorId ?? null}
        sensorLabel={unlinkTarget?.label ?? ""}
        open={!!unlinkTarget}
        onClose={() => setUnlinkTarget(null)}
        onUnlinkOverride={
          isDraft
            ? async () => {
                if (!unlinkTarget) return;
                await bridge.unlinkSensor(unlinkTarget.linkId);
                toast.success("Sensor desvinculado do rascunho.");
              }
            : undefined
        }
      />
    </div>
  );
}
